// ============================================================================
// LA FICHE MAGASIN — ses règles, sans écran (CADRAGE-SuperAdmin « Fiche magasin »).
//
// Ce module dit QUOI montrer ; `StoreDetail.tsx` dit COMMENT. Tenues à part, ces règles s'éprouvent sans monter la page :
// la disposition selon la largeur, les sept sections, les blocages qui touchent un magasin, l'alerte unique de la vue
// d'ensemble et le tableau des compteurs.
// ============================================================================
import type { IconName } from '@orgazone/ui-tokens';
import type { BlockRow } from '../api';
import { fmtDuration, type StoreHealth } from '../sync-health';

export { fmtDuration };

// ── Disposition ──────────────────────────────────────────────────────────────────────────────────

export type StoreLayout = 'three' | 'two' | 'one';

/**
 * Trois colonnes (sections · contenu · carte) à partir de 1060 px, la carte au-dessus du contenu de 700 à 1060 px, tout
 * empilé en dessous.
 * ⚠️ LA LARGEUR DE LA PAGE, PAS CELLE DE LA FENÊTRE : le menu de la Console prend déjà sa place, et une fenêtre de
 *    1280 px laisse moins de 1060 px à la page.
 */
export function layoutForWidth(width: number): StoreLayout {
  return width >= 1060 ? 'three' : width >= 700 ? 'two' : 'one';
}

// ── Sections ─────────────────────────────────────────────────────────────────────────────────────

export type StoreSection = 'overview' | 'equip' | 'updates' | 'settings' | 'data' | 'blocks' | 'history';

/** Les sept sections, dans l'ordre du menu ; leur titre et leur phrase d'usage coiffent la page. */
export const STORE_SECTIONS: readonly { key: StoreSection; icon: IconName; title: string; desc: string }[] = [
  { key: 'overview', icon: 'dashboard', title: 'Vue d’ensemble', desc: 'L’état du magasin en un coup d’œil : ce qui demande une action, ses caisses, ses compteurs.' },
  { key: 'equip', icon: 'tabletWide', title: 'Équipements', desc: 'Les caisses et terminaux affectés à ce magasin, avec leur régime de parc.' },
  { key: 'updates', icon: 'download', title: 'Mises à jour', desc: 'La version de chaque application installée sur les appareils du magasin, et le groupe qu’ils suivent.' },
  { key: 'settings', icon: 'sliders', title: 'Réglages', desc: 'Ce qui règle le fonctionnement du magasin. Les réglages d’Orgazone ne sont pas visibles du client.' },
  { key: 'data', icon: 'layers', title: 'Données', desc: 'La base du magasin : déclarations, sauvegardes, reprises et gestes irréversibles.' },
  { key: 'blocks', icon: 'ban', title: 'Blocages', desc: 'Ce qui bloque ou peut bloquer ce magasin, et ce qui l’a déjà fait.' },
  { key: 'history', icon: 'history', title: 'Historique', desc: 'Toutes les opérations de la Console sur ce magasin.' },
];

export const sectionOf = (key: StoreSection) => STORE_SECTIONS.find((s) => s.key === key)!;

// ── Blocages ─────────────────────────────────────────────────────────────────────────────────────

/**
 * Les blocages qui touchent CE magasin : les siens, ceux de ses appareils, et l'abonnement de son client.
 * ⚠️ L'abonnement compte : un client bloqué arrête toutes ses caisses, celles de ce magasin comprises — le taire ferait
 *    lire « aucun blocage » sur un magasin à l'arrêt.
 */
export function blocksTouchingStore(blocks: readonly BlockRow[], storeId: string, deviceIds: ReadonlySet<string>, clientId: string): BlockRow[] {
  return blocks.filter((b) =>
    (b.scope === 'store' && b.targetId === storeId)
    || (b.scope === 'device' && deviceIds.has(b.targetId))
    || (b.scope === 'subscription' && (b.targetId === clientId || b.tenantId === clientId)));
}

// ── Caisses et synchronisation ───────────────────────────────────────────────────────────────────

/**
 * Le numéro d'une caisse, tel qu'on le compare.
 * ⚠️ LA MÊME CAISSE EST « 1 » À L'ANNUAIRE ET « 01 » DANS LA BASE MAGASIN (la caisse normalise à deux chiffres) : les
 *    comparer en chaînes ferait deux caisses d'une seule.
 */
export const registerKey = (id: string | number | null | undefined): string | null => {
  const n = Number.parseInt(String(id ?? ''), 10);
  return Number.isFinite(n) ? String(n) : null;
};

/** L'appareil le plus muet du magasin, s'il a dépassé un seuil — `null` si tous parlent. Lu dans `storeHealth`, jamais recalculé. */
export function laggingDevice(h: StoreHealth | null): { code: string; hours: number; level: 'da' | 'wa'; till: boolean } | null {
  if (!h?.worst || (h.kind !== 'da' && h.kind !== 'wa')) return null;
  return { code: h.worst.code, hours: h.worst.hours, level: h.kind, till: h.worst.till };
}

export interface OverviewAlert { tone: 'da' | 'wa'; title: string; text: string; cta: string; go: StoreSection }

/**
 * L'ALERTE UNIQUE de la vue d'ensemble, la plus importante : un blocage en cours (le blocage avant l'avertissement), sinon
 * une caisse hors ligne, sinon une caisse silencieuse.
 * ⚠️ UNE SEULE : deux bandeaux rivalisent, et l'œil saute celui du bas. Les autres faits restent dans leur section.
 * ⚠️ Les délais cités sont ceux de la synchro périodique DU MAGASIN, lus dans sa base — `null` quand la règle est
 *    éteinte ou illisible, et la phrase le dit au lieu d'inventer un délai.
 */
export function overviewAlert(input: {
  blocks: readonly BlockRow[];
  lagging: { code: string; hours: number; level: 'da' | 'wa'; till: boolean } | null;
  rule: { on: boolean; warnDays: number; lockDays: number } | null;
}): OverviewAlert | null {
  const lead = input.blocks.find((b) => b.level === 'lock') ?? input.blocks[0];
  if (lead) {
    return {
      tone: lead.level === 'lock' ? 'da' : 'wa',
      title: `${lead.level === 'lock' ? 'Bloqué' : 'Avertissement'} — ${lead.label}`,
      text: lead.reason,
      cta: 'Voir les blocages',
      go: 'blocks',
    };
  }
  const l = input.lagging;
  if (!l) return null;
  // ⚠️ UN TERMINAL DE STOCK N'ENCAISSE PAS : ni « ses ventes », ni la règle de synchro périodique des caisses.
  if (!l.till) {
    return {
      tone: l.level,
      title: `L’appareil ${l.code} n’a rien échangé depuis ${fmtDuration(l.hours)}`,
      text: 'Ses comptages restent sur l’appareil et repartiront à la reconnexion.',
      cta: 'Voir les équipements',
      go: 'equip',
    };
  }
  const rule = input.rule;
  const consequence = rule === null
    ? 'La règle de synchro périodique du magasin n’a pas pu être lue.'
    : !rule.on
      ? 'La synchro périodique est désactivée pour ce magasin : la caisse ne sera ni avertie ni bloquée.'
      : `Sans synchronisation pendant ${rule.warnDays} jours, un avertissement s’affichera en caisse ; à ${rule.lockDays} jours, elle sera bloquée.`;
  return {
    tone: l.level,
    title: l.level === 'da'
      ? `La caisse ${l.code} ne se synchronise plus depuis ${fmtDuration(l.hours)}`
      : `La caisse ${l.code} n’a rien échangé depuis ${fmtDuration(l.hours)}`,
    text: `Elle encaisse hors ligne et garde ses ventes, qui repartiront à la reconnexion. ${consequence}`,
    cta: 'Voir les équipements',
    go: 'equip',
  };
}

// ── Compteurs ────────────────────────────────────────────────────────────────────────────────────

/**
 * Les documents numérotés magasin · caisse, dans l'ordre de la fiche. `kind` est celui de `fiscal_sequence` ; les clients
 * vivent dans `business_sequence` (`CLIENT`).
 * ⚠️ DEUX NATURES DE NUMÉROTATION, DITES À CHAQUE LIGNE : celle des pièces est FISCALE (continue, aucune remise à zéro),
 *    celle des retouches et des clients est MÉTIER (un trou y est toléré).
 */
export const COUNTER_ROWS: readonly { kind: string; label: string; nature: string }[] = [
  { kind: 'PIECE', label: 'Tickets', nature: 'séquence fiscale' },
  { kind: 'FACTURE', label: 'Factures', nature: 'séquence fiscale' },
  { kind: 'TITRE_AV', label: 'Avoirs', nature: 'séquence fiscale' },
  { kind: 'CLOTURE', label: 'Clôtures', nature: 'séquence fiscale — une par jour et par caisse' },
  { kind: 'SERVICE', label: 'Retouches', nature: 'séquence magasin · caisse' },
  { kind: 'CLIENT', label: 'Clients', nature: 'fiches créées — numérotées magasin · caisse' },
];

export interface CounterTable { cols: string[]; rows: { label: string; nature: string; cells: (number | null)[]; total: number }[] }

/**
 * Le tableau des compteurs : une colonne par caisse, plus le total.
 * ⚠️ Une case sans séquence est `null` (« — ») et non zéro : la caisse n'a encore rien numéroté de ce type, ce qui n'est
 *    pas la même chose qu'un dernier numéro valant 0.
 */
export function counterTable(fiscal: readonly { register_id: string; kind: string; last_seq: number }[], clients: readonly { register_id: string; last_seq: number }[]): CounterTable {
  const all = [...fiscal, ...clients.map((c) => ({ ...c, kind: 'CLIENT' }))];
  const cols = [...new Set(all.map((r) => r.register_id))].sort((a, b) => {
    const na = Number.parseInt(a, 10); const nb = Number.parseInt(b, 10);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return Number.isFinite(na) ? -1 : Number.isFinite(nb) ? 1 : a.localeCompare(b);
  });
  return {
    cols,
    rows: COUNTER_ROWS.map((r) => {
      const cells = cols.map((c) => all.find((x) => x.kind === r.kind && x.register_id === c)?.last_seq ?? null);
      return { label: r.label, nature: r.nature, cells, total: cells.reduce<number>((a, v) => a + (v ?? 0), 0) };
    }),
  };
}
